// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Operators inspect recent job status and errors here after a summary
 * card signals a problem. Keep the shared DataTable behavior for sorting and
 * columns, and limit dashboard-specific changes to wrapper and descendant
 * styles. Forking the table or widening its API for visual spacing would make
 * this diagnostic list behave differently from other administration tables.
 */
import { Loader2 } from 'lucide-react'
import { useRecentJobs } from "../../hooks/useJobs"
import { Alert, AlertDescription, DataTable } from '@/components/ui'
import { createJobColumns, Job } from "./columns"

interface RecentJobsTableProps {
  month: string;
  org_id?: string;
  ws_id?: string;
  metadata_fields?: Record<string, string>;
}

export function RecentJobsTable({month, org_id, ws_id, metadata_fields }: RecentJobsTableProps) {
  const { data, isLoading, error } = useRecentJobs( org_id, ws_id, month)

  // Create columns with metadata fields and context information
  const columns = createJobColumns(metadata_fields, { org_id, ws_id })

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
      <div className="flex items-center justify-between border-b border-[#eef2f6] px-6 py-4">
        <div className="text-sm font-semibold tracking-[-0.01em]">Recent jobs</div>
        {data?.items.length ? (
          <div className="text-xs text-slate-400 tabular-nums">{data.items.length} jobs this month</div>
        ) : null}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 px-6 py-12">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          <span className="text-[13px] text-slate-500">Loading jobs…</span>
        </div>
      ) : error ? (
        <div className="px-6 py-4">
          <Alert variant="destructive">
            <AlertDescription>
              Failed to load jobs. Please try again later.
            </AlertDescription>
          </Alert>
        </div>
      ) : !data?.items.length ? (
        <div className="px-6 py-12 text-center">
          <div className="text-[13.5px] font-[550] text-slate-700">No jobs found</div>
          <div className="mt-1 text-[12.5px] text-slate-400">
            Jobs will appear here once processing runs in this month.
          </div>
        </div>
      ) : (
        <div className="[&_th]:h-9 [&_th]:bg-[#fafbfc] [&_th]:px-4 [&_th]:text-[11.5px] [&_th]:font-semibold [&_th]:text-slate-500 [&_td]:px-4 [&_td]:py-2.5 [&_td]:text-[12.5px] [&_tbody_tr:hover]:bg-slate-50">
          <DataTable<Job>
            columns={columns}
            data={data.items}
          />
        </div>
      )}
    </div>
  )
}
